import {Injectable, inject} from '@angular/core';
import {IActivityCreated} from '@evencel/models';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {mergeMap, tap} from 'rxjs';
import {ActivityService} from '../../services';
import * as actions from './activity.actions';

export interface ActivityStateModel {
    activity?: IActivityCreated;
}

@State<ActivityStateModel>({name: 'activity'})
@Injectable()
export class ActivityState {
    @Selector() static activity(state: ActivityStateModel) {
        return state.activity;
    }

    @Selector() static entity(state: ActivityStateModel) {
        return state.activity?.entity;
    }

    @Selector() static subType(state: ActivityStateModel) {
        return state.activity?.subType;
    }

    @Selector() static type(state: ActivityStateModel) {
        return state.activity?.type;
    }

    activityService = inject(ActivityService);

    @Action(actions.RestActivity)
    reset({patchState}: StateContext<ActivityStateModel>) {
        return patchState({activity: undefined});
    }

    @Action(actions.CreateActivity)
    create({dispatch}: StateContext<void>, {activity}: actions.CreateActivity) {
        return this.activityService
            .post(activity)
            .pipe(mergeMap(data => dispatch(new actions.GetActivityResponse(data))));
    }

    @Action(actions.UpdateActivity)
    update(
        {dispatch, getState, patchState}: StateContext<ActivityStateModel>,
        {activity, projection}: actions.UpdateActivity
    ) {
        const current = getState().activity;
        const _id = this.getAcvityId(getState);
        return this.activityService.patch({...activity, _id}, projection).pipe(
            tap(a => {
                const activity = {...current, ...a};
                patchState({activity});
            }),
            mergeMap(data => dispatch(new actions.GetActivityResponse(data)))
        );
    }

    @Action(actions.DeleteActivity)
    delete(context: StateContext<void>, {activity}: actions.DeleteActivity) {
        return this.activityService.delete(activity._id);
    }

    @Action(actions.GetActivity)
    getOne({patchState, getState}: StateContext<ActivityStateModel>, {id, projection}: actions.GetActivity) {
        const current = getState().activity || {};
        return this.activityService.getOne(id, projection).pipe(
            tap(a => {
                const activity = {...current, ...a};
                patchState({activity});
            })
        );
    }

    @Action(actions.GetCurrentActivity)
    getCurrentActivity(
        {patchState, getState}: StateContext<ActivityStateModel>,
        {projection}: actions.GetCurrentActivity
    ) {
        const current = getState().activity;
        const _id = this.getAcvityId(getState);
        return this.activityService.getOne(_id, projection).pipe(
            tap(a => {
                const activity = {...current, ...a};

                patchState({activity});
            })
        );
    }

    @Action(actions.SearchActivitys)
    get({dispatch}: StateContext<void>, {data}: actions.SearchActivitys) {
        let obs = null;
        if (data.condition) obs = this.activityService.search(data);
        else obs = this.activityService.get(data);
        return obs.pipe(tap(data => dispatch(new actions.GetActivitysResponse(data, {state: 'success'}))));
    }

    /**
     * Get the current activity id
     */
    getAcvityId(getState: () => ActivityStateModel) {
        const id = getState().activity?._id;
        if (id) return id;
        else throw new Error('Activity id not found');
    }
}
